import { WorkflowExecutionStatus } from './type';

/**
 * Temporal workflow execution statuses that indicate the workflow has failed/stopped
 */
export const WORKFLOW_FAILED_STATUSES: ReadonlySet<string> = new Set([
	WorkflowExecutionStatus.FAILED,
	WorkflowExecutionStatus.CANCELLED,
	WorkflowExecutionStatus.TERMINATED,
	WorkflowExecutionStatus.TIMED_OUT,
]);

export const WORKFLOW_DEFINITION = {
	email: {
		moduleName: 'email',
		taskQueue: 'email-workflow',
		workflows: {
			sendEmailWorkflow: {
				name: 'sendEmailWorkflow',
				taskQueue: 'email-workflow',
				workflowId: 'send-email-workflow-id',
			},
		},
	},
	example: {
		moduleName: 'example',
		taskQueue: 'example-workflow',
		workflows: {
			simpleWorkflow: {
				name: 'simpleWorkflow',
				taskQueue: 'example-workflow',
				workflowId: 'simple-workflow-id',
			},
			sendEmailWorkflow: {
				name: 'sendEmailWorkflow',
				taskQueue: 'example-workflow',
				workflowId: 'send-email-workflow-id',
			},
		},
	},
	kyc: {
		moduleName: 'kyc',
		taskQueue: 'kyc-workflow',
		workflows: {
			investorKYCApprovalWorkflow: {
				name: 'investorKYCApprovalWorkflow',
				taskQueue: 'kyc-workflow',
				workflowId: 'investor-kyc-approval-id',
			},
		},
	},
	mip: {
		moduleName: 'mip',
		taskQueue: 'mip-workflow',
		workflows: {
			mipDownloadDocumentsExcelFileWorkflow: {
				name: 'mipDownloadDocumentsExcelFileWorkflow',
				taskQueue: 'mip-download-documents-excel-file-workflow',
				workflowId: 'mip-download-documents-excel-file-workflow-id',
			},
		},
	},
	valuation: {
		moduleName: 'valuation',
		taskQueue: 'valuation-workflow',
		workflows: {
			calculateValuationWorkflow: {
				name: 'calculateValuationWorkflow',
				taskQueue: 'valuation-workflow',
				workflowIdPrefix: 'calculate-valuation',
			},
		},
	},
	transactions: {
		moduleName: 'transactions',
		taskQueue: 'transactions-workflow',
		workflows: {
			batchTransactionUploadWorkflow: {
				name: 'batchTransactionUploadWorkflow',
				taskQueue: 'transactions-workflow',
				workflowIdPrefix: 'batch-transaction-upload',
			},
		},
	},
	documentExtraction: {
		moduleName: 'documentExtraction',
		taskQueue: 'onboarding-extraction',
		workflows: {
			documentPipeline: {
				name: 'documentPipeline',
				taskQueue: 'onboarding-extraction',
				workflowIdPrefix: 'pipeline',
			},
		},
	},
	bulkSigning: {
		moduleName: 'bulkSigning',
		taskQueue: 'bulk-signing-workflow',
		workflows: {
			bulkFtnSignWorkflow: {
				name: 'bulkFtnSignWorkflow',
				taskQueue: 'bulk-signing-workflow',
				workflowIdPrefix: 'bulk-sign',
			},
		},
	},
	bulkPostDating: {
		moduleName: 'bulkPostDating',
		taskQueue: 'bulk-post-dating-workflow',
		workflows: {
			bulkPostDatingWorkflow: {
				name: 'bulkPostDatingWorkflow',
				taskQueue: 'bulk-post-dating-workflow',
				workflowIdPrefix: 'bulk-apply-date',
			},
		},
	},
	shareRegister: {
		moduleName: 'shareRegister',
		taskQueue: 'share-register-workflow',
		// Render/stamp/merge activities run on a SEPARATE queue, polled by an
		// activity-only worker hosted in the task-processor (which ships the
		// fonts/logo + render code). Pull-based concurrency = exact CPU
		// backpressure; segments spread across task-processor replicas.
		renderTaskQueue: 'share-register-render',
		workflows: {
			generateShareRegisterPdfWorkflow: {
				name: 'generateShareRegisterPdfWorkflow',
				taskQueue: 'share-register-workflow',
				workflowIdPrefix: 'share-register-pdf',
			},
		},
	},
	bulkAssignCounterparty: {
		moduleName: 'bulkAssignCounterparty',
		taskQueue: 'bulk-assign-counterparty-workflow',
		workflows: {
			bulkAssignCounterpartyWorkflow: {
				name: 'bulkAssignCounterpartyWorkflow',
				taskQueue: 'bulk-assign-counterparty-workflow',
				workflowIdPrefix: 'bulk-assign-counterparty',
			},
		},
	},
	bulkAddCounterparty: {
		moduleName: 'bulkAddCounterparty',
		taskQueue: 'bulk-add-counterparty-workflow',
		workflows: {
			bulkAddCounterpartyWorkflow: {
				name: 'bulkAddCounterpartyWorkflow',
				taskQueue: 'bulk-add-counterparty-workflow',
				workflowIdPrefix: 'bulk-add-counterparty',
			},
		},
	},
	bulkRemindSigners: {
		moduleName: 'bulkRemindSigners',
		taskQueue: 'bulk-remind-signers-workflow',
		workflows: {
			bulkRemindSignersWorkflow: {
				name: 'bulkRemindSignersWorkflow',
				taskQueue: 'bulk-remind-signers-workflow',
				workflowIdPrefix: 'bulk-remind-signers',
			},
		},
	},
	bulkClaimGeneration: {
		moduleName: 'bulkClaimGeneration',
		taskQueue: 'bulk-claim-generation-workflow',
		workflows: {
			bulkClaimGenerationWorkflow: {
				name: 'bulkClaimGenerationWorkflow',
				taskQueue: 'bulk-claim-generation-workflow',
				workflowIdPrefix: 'bulk-claim-generation',
			},
		},
	},
	investorInvite: {
		moduleName: 'investorInvite',
		taskQueue: 'investor-invite-workflow',
		workflows: {
			investorInviteWorkflow: {
				name: 'investorInviteWorkflow',
				taskQueue: 'investor-invite-workflow',
				workflowId: 'investor-invite-workflow-id',
			},
		},
	},
	downloadInvestorsData: {
		moduleName: 'downloadInvestorsData',
		taskQueue: 'download-investors-data-workflow',
		workflows: {
			downloadInvestorsDataWorkflow: {
				name: 'downloadInvestorsDataWorkflow',
				taskQueue: 'download-investors-data-workflow',
				workflowIdPrefix: 'download-investors-data',
			},
		},
	},
	bulkDownloadFiles: {
		moduleName: 'bulkDownloadFiles',
		taskQueue: 'bulk-download-files-workflow',
		workflows: {
			bulkDownloadFilesWorkflow: {
				name: 'bulkDownloadFilesWorkflow',
				taskQueue: 'bulk-download-files-workflow',
				workflowIdPrefix: 'bulk-download-files',
			},
		},
	},
	programmeInvitations: {
		moduleName: 'programmeInvitations',
		taskQueue: 'programme-invitations-workflow',
		workflows: {
			programmeInvitationsWorkflow: {
				name: 'programmeInvitationsWorkflow',
				taskQueue: 'programme-invitations-workflow',
				workflowIdPrefix: 'programme-invitations',
			},
		},
	},
	investorTriggerSync: {
		moduleName: 'investorTriggerSync',
		taskQueue: 'investor-trigger-sync-workflow',
		workflows: {
			investorTriggerSyncWorkflow: {
				name: 'investorTriggerSyncWorkflow',
				taskQueue: 'investor-trigger-sync-workflow',
				workflowIdPrefix: 'investor-trigger-sync',
			},
		},
	},
	releaseInvestment: {
		moduleName: 'releaseInvestment',
		taskQueue: 'release-investment-workflow',
		workflows: {
			releaseInvestmentWorkflow: {
				name: 'releaseInvestmentWorkflow',
				taskQueue: 'release-investment-workflow',
				workflowIdPrefix: 'release-investment',
			},
		},
	},
	emailSchedulerTick: {
		moduleName: 'emailSchedulerTick',
		taskQueue: 'email-scheduler-tick-workflow',
		workflows: {
			emailSchedulerTickWorkflow: {
				name: 'emailSchedulerTickWorkflow',
				taskQueue: 'email-scheduler-tick-workflow',
				// Singleton — a fixed workflowId (not a per-job prefix) so the boot-time
				// starter and continueAsNew loop always resolve to the one running instance.
				workflowId: 'email-scheduler-tick',
			},
		},
	},
};

/**
 * Creates a unique workflow ID for batch transaction upload
 */
export function createBatchTransactionUploadWorkflowId(taskId: string): string {
	return `${WORKFLOW_DEFINITION.transactions.workflows.batchTransactionUploadWorkflow.workflowIdPrefix}-${taskId}`;
}

/**
 * Creates a unique workflow ID for document extraction pipeline
 */
export function createDocumentPipelineWorkflowId(documentId: string, uuid: string): string {
	return `${WORKFLOW_DEFINITION.documentExtraction.workflows.documentPipeline.workflowIdPrefix}::${documentId}::${uuid}`;
}

/**
 * Creates a unique workflow ID for bulk (FTN) signing
 */
export function createBulkSignWorkflowId(jobId: string): string {
	return `${WORKFLOW_DEFINITION.bulkSigning.workflows.bulkFtnSignWorkflow.workflowIdPrefix}-${jobId}`;
}

export function createShareRegisterPdfWorkflowId(taskId: string): string {
	return `${WORKFLOW_DEFINITION.shareRegister.workflows.generateShareRegisterPdfWorkflow.workflowIdPrefix}-${taskId}`;
}

/**
 * Creates a unique workflow ID for the investor-invite workflow
 */
export function createInvestorInviteWorkflowId(correlationId: string): string {
	return `${WORKFLOW_DEFINITION.investorInvite.workflows.investorInviteWorkflow.workflowId}-${correlationId}`;
}

/**
 * Creates a unique workflow ID for the download-investors-data workflow
 */
export function createDownloadInvestorsDataWorkflowId(correlationId: string): string {
	return `${WORKFLOW_DEFINITION.downloadInvestorsData.workflows.downloadInvestorsDataWorkflow.workflowIdPrefix}-${correlationId}`;
}

/**
 * Creates a unique workflow ID for one calculate-valuation trigger. Unique per trigger (a fresh token
 * each time) so a reload/recompute starts its own workflow + its own Redis progress stream; the id
 * doubles as the Redis stream key the backend relays from. WAT-730.
 */
export function createCalculateValuationWorkflowId(token: string): string {
	return `${WORKFLOW_DEFINITION.valuation.workflows.calculateValuationWorkflow.workflowIdPrefix}-${token}`;
}

export function createBulkDownloadFilesWorkflowId(correlationId: string): string {
	return `${WORKFLOW_DEFINITION.bulkDownloadFiles.workflows.bulkDownloadFilesWorkflow.workflowIdPrefix}-${correlationId}`;
}

export function createMipCompletionSyncWorkflowId(mipId: string): string {
	return `${WORKFLOW_DEFINITION.programmeInvitations.workflows.programmeInvitationsWorkflow.workflowIdPrefix}-mip-complete-${mipId}`;
}

/**
 * Creates the per-MIP workflow ID used to coalesce investor-trigger resyncs.
 * Falls back to an `-all` key when no mipId is provided (parity with the old
 * optional-mipId SQS path; no current producer omits mipId).
 */
export function createInvestorTriggerSyncWorkflowId(mipId?: string): string {
	return `${WORKFLOW_DEFINITION.investorTriggerSync.workflows.investorTriggerSyncWorkflow.workflowIdPrefix}-${mipId ?? 'all'}`;
}
